import { useState, useEffect } from 'react';
import axios from 'axios';
import { SealCheck, SealWarning } from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function Verify() {
  const [code, setCode] = useState('');
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const check = async (value) => {
    const v = (value ?? code).trim();
    if (!v) return;
    setBusy(true); setResult(null);
    try {
      let { data } = await axios.get(`${API}/api/acad/transcript-verify/${encodeURIComponent(v)}`);
      if (!data.valid) ({ data } = await axios.get(`${API}/api/gb/verify/${encodeURIComponent(v)}`));
      setResult(data);
    } catch {
      setResult({ valid: false, message: 'Verification is unavailable right now - try again shortly' });
    }
    setBusy(false);
  };
  useEffect(() => {
    const qc = new URLSearchParams(window.location.search).get('code');
    if (qc) { setCode(qc); check(qc); }
  }, []); // eslint-disable-line
  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6" data-testid="verify-page">
      <div className="bg-white border border-slate-200 rounded-xl p-8 w-full max-w-md shadow-sm">
        <h1 className="font-display text-xl font-extrabold tracking-tight mb-1">Document verification</h1>
        <p className="text-sm text-slate-500 mb-4">Paste the verification code printed on an official transcript or credential to confirm it is genuine.</p>
        <div className="flex gap-2">
          <input data-testid="verify-code-input" className="flex-1 border border-slate-200 rounded-md px-3 py-2 text-sm font-mono focus:border-orange-500 focus:outline-none"
            placeholder="Verification code" value={code} onChange={(e) => setCode(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && check()} />
          <button data-testid="verify-check-btn" disabled={busy || !code.trim()} onClick={() => check()}
            className="bg-slate-900 hover:bg-slate-800 text-white text-sm rounded-md px-4 py-2 disabled:opacity-50">{busy ? 'Checking...' : 'Verify'}</button>
        </div>
        {result && (
          <div data-testid="verify-result" className={`mt-4 border rounded-lg p-4 text-sm ${result.valid ? 'border-green-200 bg-green-50' : 'border-rose-200 bg-rose-50'}`}>
            {result.valid ? (
              <>
                <p className="flex items-center gap-1.5 font-semibold text-green-700 mb-2"><SealCheck size={18} weight="fill" /> Genuine document</p>
                <p><b>{result.serial}</b>{result.title ? ` - ${result.title}` : result.kind === 'transcript' ? ' - Academic transcript' : ''}</p>
                <p>Holder: <b>{result.student_name}</b></p>
                {result.institution && <p>Issued by {result.institution}</p>}
                <p className="text-xs text-slate-500 mt-1">Issued {(result.issued_at || '').slice(0, 10)}</p>
                {result.credits_earned != null && <p className="text-xs text-slate-500">{result.credits_earned} credits earned{result.cumulative_gpa != null ? ` - GPA ${result.cumulative_gpa}` : ''} at the time of issue</p>}
              </>
            ) : (
              <p className="flex items-center gap-1.5 text-rose-600"><SealWarning size={18} weight="fill" /> {result.message || 'This code does not match any issued document.'}</p>
            )}
          </div>
        )}
        <a href="/login" className="block text-xs text-slate-400 hover:text-slate-600 mt-5">ONRID - Know everyone. Trust the record.</a>
      </div>
    </div>
  );
}
